import { call, put, takeEvery, select } from 'redux-saga/effects'
import axios from 'axios'

import api from '~/services/api'
import {
	verifyToken,
	verifyTokenSuccess,
	verifyTokenFailure,
	createContractExternal,
	createContractExternalSuccess,
	createContractExternalFailure,
	downloadExternalDocument,
	downloadExternalDocumentSuccess,
	downloadExternalDocumentFailure,
} from '.'
import { listQuestionSuccess } from '../question'
import {
	loadingMessage,
	successMessage,
	errorMessage,
} from '~/services/messager'
import { selectVisibleAnswers } from '../answer/selectors'

export default function* rootSaga() {
	yield takeEvery(verifyToken, verifyTokenSaga)
	yield takeEvery(createContractExternal, createContractExternalSaga)
	yield takeEvery(downloadExternalDocument, downloadExternalDocumentSaga)
}

function* verifyTokenSaga({ payload = {} }) {
	const { token } = payload

	try {
		const { data } = yield call(api.post, `/external/authorize`, {
			token,
		})

		yield put(
			verifyTokenSuccess({
				data: { ...data, token, ...data.Template },
			})
		)

		// data.Authorized is false (token missing/already used/template deleted)
		// when data.Template isn't present - nothing left to do, the component
		// renders the "already used" message from `authorized`/`created` state.
		if (data.Template) {
			const modelId = data.Template.id
			const title = 'Meu processo'
			const parent = null
			const templateData = data.Template
			yield put(
				listQuestionSuccess({ modelId, title, parent, data: templateData })
			)
		}
	} catch (error) {
		yield put(verifyTokenFailure(error))
	}
}

function* createContractExternalSaga({ payload }) {
	loadingMessage({
		content: 'Nossos robôs estão trabalhando para gerar seu documento',
		updateKey: 'createContractExternal',
	})

	const { answer, visible, uploadedFile, pages } = yield select((state) => {
		const {
			answer,
			question: { visible, uploadedFile, data: pages },
		} = state
		return { answer, visible, uploadedFile, pages }
	})

	const { token, external_id, title, external_user, draft } = payload

	try {
		const { data } = yield call(api.post, '/external/create', {
			token,
			external_id,
			title,
			external_user,
			uploadedFile,
			variables: selectVisibleAnswers(answer.data, pages, visible),
			visible,
			draft,
		})

		yield put(
			createContractExternalSuccess({
				documentId: data.id,
				documentData: data,
			})
		)
		successMessage({
			content: 'Documento criado com sucesso!',
			updateKey: 'createContractExternal',
		})
	} catch (error) {
		yield put(createContractExternalFailure(error))
		errorMessage({
			content: 'A criação do documento falhou',
			updateKey: 'createContractExternal',
		})
	}
}

function* downloadExternalDocumentSaga({ payload }) {
	const { documentId, token, textType, versionId, filename } = payload
	loadingMessage({
		content: 'Realizando download do documento...',
		updateKey: 'downloadExternalDocument',
	})
	try {
		if (textType === '.txt') {
			const { data } = yield call(api.get, `/external/${documentId}/pdf`, {
				params: { token, version: versionId, download: 1 },
			})
			window.location.assign(data.download_url)
		} else {
			const { data } = yield call(api.get, `/external/${documentId}/docx`, {
				params: { token },
			})
			const response = yield call(axios.get, data.download_url, {
				responseType: 'blob',
			})
			const blob = new Blob([response.data], {
				type: response.headers['content-type'],
			})
			const href = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.setAttribute('href', href)
			link.setAttribute('download', filename || 'document.docx')
			document.body.appendChild(link)
			link.click()
			link.remove()
		}
		yield put(downloadExternalDocumentSuccess())
		successMessage({
			content: 'Documento baixado com sucesso!',
			updateKey: 'downloadExternalDocument',
		})
	} catch (error) {
		yield put(downloadExternalDocumentFailure(error))
		errorMessage({
			content: 'Ocorreu um erro ao baixar o seu documento.',
			updateKey: 'downloadExternalDocument',
		})
	}
}
